// Share a file or folder: who gets access, at what level, how far into a
// folder, with or without download & print, until when. Everything the
// dialog offers comes from GET /share/node/{node_uuid}: the caller's own
// access (a manager passes on at most that), the install's `sharing` policy
// (which levels, reaches and dates may be chosen) and the people who already
// have access — each changeable or removable when the caller may. People are
// the install's directory (GET /share/all-users), named by docs' uuid. The
// Guest link tab makes, lists and revokes links anyone can open without an
// account (files; folders where the install allows) — a new link is shown
// once, only its hash is kept.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Copy, Loader2, Search, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDeleteRequest, useGetRequest, usePatchRequest, usePostRequest } from '@/shared/hooks/requests.js';
import { GUEST_LINK, GUEST_LINKS, NODE_SHARES, SHARE_FILE, SHARE_ITEM, SHARE_USERS } from '@/shared/utils/urls.js';
import { currentUserUuid } from '@/shared/auth/me.js';
import { nameOf } from '../people.js';
import { dateInput, endOfDay, expiryBounds, expiryProblem } from '../shareExpiry.js';
import { FieldError } from '@/shared/ui/FieldError.jsx';

const GUEST_LABEL_MAX = 120;
const LEVELS = ['viewer', 'commenter', 'contributor', 'editor', 'manager'];
const REACHES = ['subtree', 'files', 'self'];

/** True when the options can't be sent: a date is required and missing, or the date is out of range. */
const expiryBlocked = (value, policy, own) => Boolean(
  ((policy?.requireExpiry || own?.expiresAt) && !value?.expires)
  || expiryProblem(value?.expires, expiryBounds(policy, own)),
);

function Options({ t, isFolder, policy, own, value, onChange, hideLevel = false }) {
  const levels = LEVELS.filter((l) => (policy?.levelsAllowed || LEVELS).includes(l));
  const reaches = REACHES.filter((r) => (policy?.reachesAllowed || REACHES).includes(r));
  // From tomorrow to the earliest of the install's limit and my own access.
  const { min, max } = expiryBounds(policy, own);
  const problem = expiryProblem(value.expires, { min, max });
  // Download may only be off where the install allows it, only on where I
  // may download myself.
  const downloadLocked = !policy?.allowDownloadOff || own?.download === false;
  const set = (key, v) => onChange({ ...value, [key]: v });

  return (
    <div className="space-y-3" data-testid="share-options">
      {!hideLevel && <div className="space-y-1">
        <Label>{t('shareModal:access.level.label')}</Label>
        <Select value={value.level} onValueChange={(v) => set('level', v)}>
          <SelectTrigger aria-label={t('shareModal:access.level.label')} data-testid="share-level"><SelectValue /></SelectTrigger>
          <SelectContent className="z-[300]">
            {levels.map((l) => (
              <SelectItem key={l} value={l}>
                <span className="flex flex-col text-left">
                  <span>{t(`shareModal:access.level.${l}.name`)}</span>
                  <span className="text-xs text-muted-foreground">{t(`shareModal:access.level.${l}.hint`)}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>}
      {isFolder && (
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">{t('shareModal:access.reach.label')}</legend>
          {reaches.map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm">
              <input type="radio" name="share-reach" value={r} checked={value.reach === r} onChange={() => set('reach', r)} />
              {t(`shareModal:access.reach.${r}`)}
            </label>
          ))}
        </fieldset>
      )}
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={value.allowDownload}
            disabled={downloadLocked}
            onCheckedChange={(c) => set('allowDownload', c === true)}
            aria-label={t('shareModal:access.download.label')}
          />
          {t('shareModal:access.download.label')}
        </label>
        <p className="text-xs text-muted-foreground">{t('shareModal:access.download.hint')}</p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="share-expires">
          {policy?.requireExpiry || own?.expiresAt ? t('shareModal:access.expires.required') : t('shareModal:access.expires.label')}
        </Label>
        <Input
          id="share-expires"
          data-testid="share-expires"
          type="date"
          min={min}
          max={max}
          value={value.expires}
          aria-invalid={Boolean(problem) || undefined}
          aria-describedby={problem ? 'share-expires-error' : undefined}
          onChange={(e) => set('expires', e.target.value)}
        />
        <FieldError
          id="share-expires-error"
          message={problem ? t(`shareModal:access.expires.problem.${problem}`, { min, max }) : null}
        />
      </div>
    </div>
  );
}

function ShareRow({ t, row, isFolder, policy, own, ownerUuid, onSaved, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const patch = usePatchRequest({ url: SHARE_ITEM.replace('{share_uuid}', row.uuid) });
  const remove = useDeleteRequest({ url: SHARE_ITEM.replace('{share_uuid}', row.uuid) });

  const summary = [
    t(`shareModal:access.level.${row.level}.name`),
    isFolder ? t(`shareModal:access.reach.${row.reach}`) : null,
    row.allowDownload ? null : t('shareModal:access.list.noDownload'),
    row.expiresAt
      ? t(row.expired ? 'shareModal:access.expires.expired' : 'shareModal:access.expires.until', { date: new Date(row.expiresAt).toLocaleDateString() })
      : null,
  ].filter(Boolean).join(' · ');

  const start = () => {
    setDraft({ level: row.level, reach: row.reach, allowDownload: row.allowDownload, expires: dateInput(row.expiresAt) });
    setOpen(true);
  };
  const save = async () => {
    const { success } = await patch.request({
      data: { level: draft.level, reach: draft.reach, allowDownload: draft.allowDownload, expiresAt: endOfDay(draft.expires, policy?.maxShareDays) },
    });
    if (!success) return;
    toast.success(t('shareModal:access.saved'));
    setOpen(false);
    onSaved();
  };
  const drop = async () => {
    const { success } = await remove.request();
    if (!success) return;
    toast.success(t('shareModal:access.removed'));
    onRemoved();
  };

  return (
    <li className="rounded-md border border-border p-2 space-y-2" data-testid={`share-row-${row.user?.uuid}`}>
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
          {nameOf(row.user).charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{nameOf(row.user)}</div>
          <div className={`text-xs ${row.expired ? 'text-destructive' : 'text-muted-foreground'}`}>{summary}</div>
          {row.sharedBy && String(row.sharedBy.uuid) !== String(ownerUuid) && (
            <div className="text-xs text-muted-foreground">{t('shareModal:access.list.addedBy', { name: nameOf(row.sharedBy) })}</div>
          )}
        </div>
        {row.canChange && !open && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={start}>{t('shareModal:access.list.change')}</Button>
            <Button variant="ghost" size="icon" onClick={drop} disabled={remove.loading} aria-label={t('shareModal:access.list.remove')}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      {open && draft && (
        <div className="space-y-3 border-t border-border pt-2">
          <Options t={t} isFolder={isFolder} policy={policy} own={own} value={draft} onChange={setDraft} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>{t('shareModal:share.modal.btn.cancel')}</Button>
            <Button size="sm" onClick={save} disabled={patch.loading || expiryBlocked(draft, policy, own)}>
              {patch.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('shareModal:access.list.save')}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Pick people of the install (GET /share/all-users?search=): rows
 * {uuid, personId, displayName, email}. `exclude` and `selected` are docs
 * uuids / rows not to offer again.
 */
export function PeoplePicker({ t, exclude, selected, onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const users = useGetRequest({ url: SHARE_USERS });
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      users.request({ params: query.trim() ? { search: query.trim() } : {} });
    }, 250);
    return () => clearTimeout(timer.current);
    // The request helper is a new object every render; the query drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  const taken = new Set([...exclude.map(String), ...selected.map((u) => String(u.uuid))]);
  const matches = (users.response || []).filter((u) => u?.uuid && !taken.has(String(u.uuid))).slice(0, 8);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
        <Input
          className="pl-8"
          value={query}
          placeholder={t('shareModal:access.add.placeholder')}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          data-testid="share-people-search"
        />
      </div>
      {open && (
        <ul className="max-h-40 overflow-y-auto rounded-md border border-border" role="listbox">
          {users.loading && !users.response && <li className="p-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></li>}
          {users.response && matches.length === 0 && <li className="p-2 text-sm text-muted-foreground">{t('shareModal:access.add.none')}</li>}
          {matches.map((u) => (
            <li key={u.uuid}>
              <button
                type="button"
                className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted"
                onClick={() => { onSelect(u); setQuery(''); }}
              >
                <span className="font-medium">{nameOf(u)}</span>
                {u.displayName && <span className="text-muted-foreground"> · {u.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * `nodeUuid`: the file or folder shared; `item` / `file`: its row (name,
 * `ownerUuid`); `type` 'folder' for a folder.
 */
export const SharingModal = ({ openShare, setOpenShare, nodeUuid, item, file, type }) => {
  const { t } = useTranslation();
  const me = currentUserUuid();
  const isFolder = type === 'folder' || item?.type === 'folder';
  const [tab, setTab] = useState('people');
  const [selected, setSelected] = useState([]);
  const [options, setOptions] = useState(null);

  const info = useGetRequest({ url: NODE_SHARES.replace('{node_uuid}', nodeUuid) });
  const share = usePostRequest({ url: SHARE_FILE });
  const data = info.response;
  const policy = data?.policy;
  const own = data?.access;
  const mayShare = (own?.can || []).includes('share');
  const ownerUuid = (item || file)?.ownerUuid ?? (item || file)?.owner_uuid;

  const reload = () => info.request();
  useEffect(() => {
    if (openShare && nodeUuid) {
      setTab('people');
      setSelected([]);
      setOptions(null);
      reload();
    }
    // `reload` is a new function every render; opening (or a new node) drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openShare, nodeUuid]);

  // The options start from the install's defaults, within my own access.
  const defaults = useMemo(() => {
    if (!policy) return null;
    return {
      level: policy.defaultLevel,
      reach: policy.defaultReach,
      allowDownload: own?.download === false ? false : policy.defaultDownload,
      expires: '',
    };
  }, [policy, own]);
  const value = options || defaults;

  const shared = data?.shares || [];
  const exclude = [me, ...shared.map((s) => s.user?.uuid)].filter(Boolean).map(String);
  // Guest links: where the install has them (folders only when it says so),
  // for those who may share.
  const showLinks = Boolean(data && mayShare && policy?.guestLinks !== false && (!isFolder || policy?.guestLinkFolders));

  const submit = async () => {
    const { success } = await share.request({
      data: {
        nodeUuid,
        sharedWithUuids: selected.map((u) => u.uuid),
        level: value.level,
        reach: value.reach,
        allowDownload: value.allowDownload,
        expiresAt: endOfDay(value.expires, policy?.maxShareDays),
      },
    });
    if (!success) return;
    toast.success(t('shareModal:access.shared'));
    setSelected([]);
    reload();
  };

  return (
    <Dialog open={openShare} onOpenChange={setOpenShare}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" zIndex={200}>
        <DialogHeader>
          <DialogTitle>{t('shareModal:share.modal.title')} · {(item || file)?.name}</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          {showLinks && (
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="people">{t('shareModal:access.people')}</TabsTrigger>
              <TabsTrigger value="link">{t('shareModal:access.link')}</TabsTrigger>
            </TabsList>
          )}
          <TabsContent value="people" className="mt-0 space-y-4">
            {info.loading && !data && <div className="flex justify-center p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>}
            {data && mayShare && value && (
              <section className="space-y-3">
                <PeoplePicker t={t} exclude={exclude} selected={selected} onSelect={(u) => setSelected((s) => [...s, u])} />
                {selected.length > 0 && (
                  <>
                    <div className="flex flex-wrap gap-1">
                      {selected.map((u) => (
                        <span key={u.uuid} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                          {nameOf(u)}
                          <button type="button" aria-label={t('shareModal:access.list.remove')} onClick={() => setSelected((s) => s.filter((x) => x.uuid !== u.uuid))}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <Options t={t} isFolder={isFolder} policy={policy} own={own} value={value} onChange={setOptions} />
                    <Button className="w-full" onClick={submit} disabled={share.loading || expiryBlocked(value, policy, own)} data-testid="share-submit">
                      {share.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {t('shareModal:access.add.share', { count: selected.length })}
                    </Button>
                  </>
                )}
              </section>
            )}
            {data && !mayShare && <p className="text-sm text-muted-foreground">{t('shareModal:access.list.readOnly')}</p>}
            {data && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">{t('shareModal:access.list.title')}</h3>
                {shared.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('shareModal:access.list.empty')}</p>
                ) : (
                  <ul className="space-y-2">
                    {shared.map((row) => (
                      <ShareRow key={row.uuid} t={t} row={row} isFolder={isFolder} policy={policy} own={own} ownerUuid={ownerUuid} onSaved={reload} onRemoved={reload} />
                    ))}
                  </ul>
                )}
              </section>
            )}
          </TabsContent>
          {showLinks && (
            <TabsContent value="link" className="mt-0">
              <GuestLinks t={t} nodeUuid={nodeUuid} isFolder={isFolder} policy={policy} own={own} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const guestUrl = (token) => `${window.location.origin}/guest-share/${token}`;

function GuestLinkRow({ t, link, isFolder, onRevoked }) {
  const revoke = useDeleteRequest({ url: GUEST_LINK.replace('{link_uuid}', link.uuid) });
  const summary = [
    isFolder ? t(`shareModal:access.reach.${link.reach}`) : null,
    link.allowDownload ? null : t('shareModal:access.list.noDownload'),
    link.expiresAt
      ? t(link.expired ? 'shareModal:access.expires.expired' : 'shareModal:access.expires.until', { date: new Date(link.expiresAt).toLocaleDateString() })
      : null,
    link.lastUsedAt ? t('shareModal:access.guest.lastUsed', { date: new Date(link.lastUsedAt).toLocaleDateString() }) : t('shareModal:access.guest.neverUsed'),
  ].filter(Boolean).join(' · ');
  const drop = async () => {
    const { success } = await revoke.request();
    if (!success) return;
    toast.success(t('shareModal:access.guest.revoked'));
    onRevoked();
  };
  return (
    <li className="flex items-start gap-2 rounded-md border border-border p-2" data-testid={`guest-link-${link.uuid}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{link.label || t('shareModal:access.guest.unnamed')}</div>
        <div className={`text-xs ${link.expired ? 'text-destructive' : 'text-muted-foreground'}`}>{summary}</div>
      </div>
      {link.canChange && (
        <Button variant="ghost" size="sm" onClick={drop} disabled={revoke.loading}>{t('shareModal:access.guest.revoke')}</Button>
      )}
    </li>
  );
}

function GuestLinks({ t, nodeUuid, isFolder, policy, own }) {
  const list = useGetRequest({ url: GUEST_LINKS });
  const make = usePostRequest({ url: GUEST_LINKS });
  const [made, setMade] = useState(null);
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState(null);
  const reload = () => list.request({ params: { nodeUuid } });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- `reload` is new every render; the node drives it
  useEffect(() => { setMade(null); setCopied(false); setDraft(null); reload(); }, [nodeUuid]);

  const start = () => setDraft({
    label: '',
    reach: 'subtree',
    allowDownload: own?.download === false ? false : policy?.defaultDownload !== false,
    expires: '',
  });
  // The guest-link rules ride the member Options control: expiry and
  // download as for people, reach limited to what a link may have.
  const linkPolicy = {
    ...policy,
    reachesAllowed: ['subtree', 'files'],
    requireExpiry: policy?.guestLinkRequireExpiry,
    maxShareDays: policy?.guestLinkMaxDays,
  };

  const create = async () => {
    const { success, response } = await make.request({
      data: {
        nodeUuid,
        label: draft.label.trim() || null,
        ...(isFolder ? { reach: draft.reach } : {}),
        allowDownload: draft.allowDownload,
        expiresAt: endOfDay(draft.expires, linkPolicy.maxShareDays),
      },
    });
    if (!success) return;
    setMade(guestUrl(response.shareToken));
    setDraft(null);
    reload();
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made);
      setCopied(true);
      toast.success(t('my-docs:share.copied'));
    } catch {
      toast.error(t('my-docs:share.linkError'));
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{t('shareModal:access.guest.hint')}</p>
      {made && (
        <div className="space-y-1" data-testid="guest-link-made">
          <div className="flex items-center gap-2">
            <Input value={made} readOnly className="flex-1" />
            <Button size="icon" variant="outline" onClick={copy} aria-label={t('my-docs:share.copied')}>
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400">{t('shareModal:access.guest.copyNow')}</p>
        </div>
      )}
      {draft ? (
        <div className="space-y-3 rounded-md border border-border p-3">
          <div className="space-y-1">
            <Label htmlFor="guest-label">{t('shareModal:access.guest.label')}</Label>
            {/* The server's limit (`resource_guest_shares.label`); blank means no label. */}
            <Input id="guest-label" value={draft.label} maxLength={GUEST_LABEL_MAX} placeholder={t('shareModal:access.guest.labelPlaceholder')} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
          </div>
          <Options
            t={t}
            isFolder={isFolder}
            policy={linkPolicy}
            own={own}
            value={{ ...draft, level: 'viewer' }}
            onChange={(v) => setDraft({ ...draft, reach: v.reach, allowDownload: v.allowDownload, expires: v.expires })}
            hideLevel
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>{t('shareModal:share.modal.btn.cancel')}</Button>
            <Button size="sm" onClick={create} disabled={make.loading || expiryBlocked(draft, linkPolicy, own)} data-testid="guest-link-create">
              {make.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('shareModal:access.guest.create')}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" className="w-full" onClick={start} data-testid="guest-link-new">{t('shareModal:access.guest.new')}</Button>
      )}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">{t('shareModal:access.guest.title')}</h3>
        {list.response && list.response.length === 0 && <p className="text-sm text-muted-foreground">{t('shareModal:access.guest.none')}</p>}
        {list.response && list.response.length > 0 && (
          <ul className="space-y-2">
            {list.response.map((link) => <GuestLinkRow key={link.uuid} t={t} link={link} isFolder={isFolder} onRevoked={reload} />)}
          </ul>
        )}
      </section>
    </div>
  );
}

export default SharingModal;
